import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';
import { handleApiError, retryRequest, retryRegistration } from '../utils/errorHandler';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar si el usuario está autenticado al cargar la aplicación
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await authService.getCurrentUser();
      setUser(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al verificar autenticación:', error);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await authService.login(credentials);
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error al iniciar sesión';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      console.log('🚀 Iniciando registro con datos:', { ...userData, password: '[PROTECTED]' });
      
      // Usar reintentos especializados para el registro
      const response = await retryRegistration(
        () => authService.register(userData),
        window.APP_CONFIG?.MAX_RETRIES || 2, // máximo 2 intentos por defecto
        5000 // esperar 5 segundos entre intentos (aumentado)
      );
      
      console.log('✅ Respuesta exitosa de registro:', response.status);
      const { token, user: newUser } = response.data;
      
      localStorage.setItem('token', token);
      setUser(newUser);
      
      return { success: true };
    } catch (error) {
      console.error('❌ Error final en registro:', error);
      const errorInfo = handleApiError(error);
      
      // Mensajes de error más descriptivos
      let userMessage = errorInfo.message;
      if (errorInfo.type === 'timeout') {
        userMessage = 'El registro está tardando más de lo esperado. El servidor podría estar sobrecargado. Por favor, intenta nuevamente en unos minutos.';
      } else if (errorInfo.type === 'network') {
        userMessage = 'Problema de conectividad. Verifica tu conexión a internet e intenta nuevamente.';
      }
      
      setError(userMessage);
      return { success: false, error: userMessage };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setError(null);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    clearError,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 